// The road's camera (FE-11): pure maths, no Pixi, so it is tested on its own.
// It follows my predicted keke with a dead zone (small wiggles don't shake the world), eases after
// it (or keeps up exactly under reduced motion), zooms in tiers by the screen's size, and never
// shows past the edge of the map. The HUD covers part of the screen (the route bar on top, the action
// discs bottom-right), so at an edge the map may slide under that cover far enough for its last row
// to show in the open, never further.
import { TILE } from '@wahala/maps';

export interface Vec2 {
  x: number;
  y: number;
}

/** The world point the camera holds at its focus (screen centre across, a little above centre down). */
export interface Camera {
  x: number;
  y: number;
  ready: boolean;
}

/** Where the camera puts the world this frame: a scale, then a translation in screen pixels. */
export interface CameraView {
  zoom: number;
  x: number;
  y: number;
}

/**
 * Tiles across the screen's short side, by that side's length in CSS pixels: a phone in portrait
 * (375 x 812) or on its side sees 9, a big phone or small tablet 11, a tablet or computer 13.
 */
export const ZOOM_TIERS: ReadonlyArray<{ upTo: number; tiles: number }> = [
  { upTo: 480, tiles: 9 },
  { upTo: 760, tiles: 11 },
  { upTo: Number.POSITIVE_INFINITY, tiles: 13 },
];
/**
 * In landscape, at most this many tiles across: a turned phone or a computer screen would otherwise
 * see half the park. Portrait keeps its tier (the tall side is where the road ahead is).
 */
export const MAX_LONG_TILES = 18;
/** Half the dead zone, in world units: the keke moves this far from the focus before the camera does. */
export const DEAD_X = 0.6 * TILE;
export const DEAD_Y = 0.8 * TILE;
/** Easing time constant (ms): the camera closes about 63% of the gap each TAU. */
export const TAU_MS = 90;
/** A jump this far (a meeting's line-up, a spawn) is a cut, not a pan. */
export const CUT_DIST = 6 * TILE;
/** Where the focus sits down the screen: a little above centre, so thumbs never cover the keke. */
export const FOCUS_Y = 0.45;

export function newCamera(): Camera {
  return { x: 0, y: 0, ready: false };
}

/** World-to-screen scale for a screen of w x h CSS pixels. */
export function zoomFor(w: number, h: number): number {
  const short = Math.max(1, Math.min(w, h));
  const long = Math.max(1, Math.max(w, h));
  const tier = ZOOM_TIERS.find((t) => short <= t.upTo) ?? (ZOOM_TIERS[ZOOM_TIERS.length - 1] as { tiles: number });
  const z = short / (tier.tiles * TILE);
  return w > h ? Math.max(z, long / (MAX_LONG_TILES * TILE)) : z;
}

/** One axis of the dead zone: where the focus must be so the target sits at or inside its edge. */
function deadAxis(focus: number, target: number, half: number): number {
  const d = target - focus;
  if (d > half) return target - half;
  if (d < -half) return target + half;
  return focus;
}

/** Move the camera after the target for one frame of dtMs. Mutates and returns cam. */
export function follow(cam: Camera, target: Vec2, dtMs: number, reduced: boolean): Camera {
  if (!cam.ready || Math.hypot(target.x - cam.x, target.y - cam.y) > CUT_DIST) {
    cam.x = target.x;
    cam.y = target.y;
    cam.ready = true;
    return cam;
  }
  const wantX = deadAxis(cam.x, target.x, DEAD_X);
  const wantY = deadAxis(cam.y, target.y, DEAD_Y);
  // reduced motion: no easing, the camera simply keeps the keke inside the dead zone
  const k = reduced ? 1 : 1 - Math.exp(-Math.max(0, dtMs) / TAU_MS);
  cam.x += (wantX - cam.x) * k;
  cam.y += (wantY - cam.y) * k;
  return cam;
}

/** Screen pixels the HUD covers along each edge (CSS pixels). */
export interface Cover {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

export const NO_COVER: Readonly<Cover> = { top: 0, right: 0, bottom: 0, left: 0 };

/**
 * One axis of the clamp: the first world unit on screen. The open part of the screen (span less the
 * cover at each end, in world units) stays inside [0, size]; a map smaller than the screen is centred.
 */
function clampAxis(start: number, span: number, size: number, lo: number, hi: number): number {
  if (span >= size) return (size - span) / 2;
  const open = span - lo - hi;
  if (open >= size) return (size - open) / 2 - lo;
  return Math.min(Math.max(start, -lo), size - span + hi);
}

/**
 * The transform for this frame: the focus at (w/2, h * FOCUS_Y), clamped so the open screen (outside
 * the HUD's cover) never shows past the map's edges, snapped to whole device pixels so the road doesn't shimmer.
 */
export function viewOf(
  cam: Camera,
  w: number,
  h: number,
  map: { width: number; height: number },
  resolution = 1,
  cover: Readonly<Cover> = NO_COVER,
): CameraView {
  const zoom = zoomFor(w, h);
  // the cover is capped at a third of the screen each way, so a tall board never lets the map drift off
  const c = (v: number, of: number) => Math.min(Math.max(0, v), of / 3) / zoom;
  const left = clampAxis(cam.x - w / 2 / zoom, w / zoom, map.width, c(cover.left, w), c(cover.right, w));
  const top = clampAxis(cam.y - (h * FOCUS_Y) / zoom, h / zoom, map.height, c(cover.top, h), c(cover.bottom, h));
  const px = (v: number) => Math.round(v * resolution) / resolution;
  return { zoom, x: px(-left * zoom), y: px(-top * zoom) };
}

/**
 * Race: a chase camera, as in a racing game. The road turns so my keke always drives up the screen.
 * `yaw` is the map direction the screen's up shows (radians, 0 east, y grows down), easing after the
 * keke's heading. Each road frames the keke from behind its own way (the 3D road's race rig, the
 * flat road's tilt).
 */
export interface Chase {
  yaw: number;
  /** How fast the yaw is turning (radians per ms): the spring's own speed. */
  spin: number;
  ready: boolean;
}
/**
 * The yaw follows on a critically damped spring: it closes about 60% of a turn each CHASE_TAU_MS,
 * starting and stopping smoothly, never overshooting, so the road swings instead of jerking (a
 * plain ease jolted at the start of every step of the heading).
 */
export const CHASE_TAU_MS = 200;
const CHASE_W = 2 / CHASE_TAU_MS;

export function newChase(): Chase {
  return { yaw: -Math.PI / 2, spin: 0, ready: false };
}

/** An angle folded into (-PI, PI]. */
const wrap = (a: number): number => a - 2 * Math.PI * Math.ceil((a - Math.PI) / (2 * Math.PI));

/**
 * Turn the chase camera after the keke's heading (radians, as `yaw`) for one frame of dtMs, unless
 * `hold` (a spin: the road stays put while the keke whirls). Reduced motion keeps up exactly, no
 * easing. Mutates and returns c.
 */
export function chase(c: Chase, heading: number, hold: boolean, dtMs: number, reduced: boolean): Chase {
  if (!c.ready) {
    c.yaw = wrap(heading);
    c.spin = 0;
    c.ready = true;
    return c;
  }
  if (hold) {
    c.spin = 0;
    return c;
  }
  if (reduced) {
    c.yaw = wrap(heading);
    c.spin = 0;
    return c;
  }
  // the exact step of a critically damped spring toward the heading (stable at any frame time)
  const t = Math.max(0, dtMs);
  const x = -wrap(heading - c.yaw);
  const e = Math.exp(-CHASE_W * t);
  const k = c.spin + CHASE_W * x;
  const nx = (x + k * t) * e;
  c.spin = (c.spin - CHASE_W * k * t) * e;
  c.yaw = wrap(heading + nx);
  return c;
}

/**
 * The stick as the screen shows it (dx right, dy down, -127..127) turned onto the map under the
 * chase camera's yaw: up the screen is where the road goes. Whole numbers, never longer than 127.
 */
export function stickOnMap(dx: number, dy: number, yaw: number): { dx: number; dy: number } {
  if (dx === 0 && dy === 0) return { dx: 0, dy: 0 };
  const ux = Math.cos(yaw);
  const uy = Math.sin(yaw);
  // screen right is the map direction a quarter turn clockwise of up
  const x = dx * -uy - dy * ux;
  const y = dx * ux - dy * uy;
  const len = Math.hypot(x, y);
  const k = len > 127 ? 127 / len : 1;
  // rounded, unless rounding would make it longer than the wire allows; never a -0
  let rx = Math.round(x * k);
  let ry = Math.round(y * k);
  if (Math.hypot(rx, ry) > 127) {
    rx = Math.trunc(x * k);
    ry = Math.trunc(y * k);
  }
  return { dx: rx || 0, dy: ry || 0 };
}
